import { useMemo } from "react";
import {
  HydratedZapAsset,
  useSplitTreatments,
  hasZapStorageMetadata,
  useZapActionPermissions,
  Zap,
} from "@zapier/client";
import {
  getCreateTemplateFromZapUrl,
  getLastRunDateRange,
  getZapHistoryUrl,
} from "@zapier/utils";
import {
  KebabMenuItem,
  useEmitZapKebabInteractionEvent,
  ZapKebabInteractionOption,
  ZapModal,
} from "@zapier/shared";
import { useTranslations } from "@zapier/i18n";

interface Input {
  zapId: string;
  zap: Zap | HydratedZapAsset;
  lastRun?: string;
  setCurrentModal: (modal: ZapModal) => void;
  setCurrentZap: (zap: Zap | HydratedZapAsset | undefined) => void;
  onCopyToAnotherAccount?: (asset: HydratedZapAsset) => void;
  isZapPurgeEligible?: boolean;
}

export function useZapRow({
  zapId,
  zap,
  lastRun,
  setCurrentModal,
  setCurrentZap,
  onCopyToAnotherAccount,
  isZapPurgeEligible,
}: Input) {
  const t = useTranslations("zap-row-actions");
  const tAction = useTranslations("buttons");
  const { isLastRunEnabled } = useSplitTreatments();

  const {
    canChangeOwner,
    canCreateTemplate,
    canDelete,
    canDuplicate,
    canMoveZapToFolder,
    canRename,
    canViewZapHistory,
    canEnableDisable,
    enableAdvancedPermissions,
  } = useZapActionPermissions({ zap });

  const { emit } = useEmitZapKebabInteractionEvent();

  const templateMode = zap.currentVersion ? "published" : "draft";

  // canDelete is hard-false for trashed zaps; the purge endpoint checks permission instead.
  const isZapDeleteEnabled =
    isZapPurgeEligible || (canDelete && hasZapStorageMetadata(zap));
  const isZapTrashed = !!zap.deletedAt;
  // A trashed zap with no purge path can never leave this disabled state (canDelete
  // is hard-false in trash), so hide the dead item instead of showing a disabled
  // "Move to trash" that reads as a no-op on something already trashed.
  const showDeleteAction = !isZapTrashed || isZapDeleteEnabled;

  const menuItems = useMemo(() => {
    return [
      {
        icon: "actionEdit",
        label: t("rename"),
        onClick: () => {
          emit(ZapKebabInteractionOption.Rename);
          setCurrentZap(zap);
          setCurrentModal(ZapModal.Rename);
        },
        disabled: !canRename,
      },
      {
        icon: "miscClock",
        label: t("view-history"),
        href: getZapHistoryUrl(
          zapId,
          isLastRunEnabled ? getLastRunDateRange(lastRun) : undefined,
        ),
        onClick: () => {
          emit(ZapKebabInteractionOption.ViewHistory);
        },
        disabled: !canViewZapHistory,
      },
      {
        icon: "actionCopy",
        label: t("duplicate"),
        onClick: () => {
          emit(ZapKebabInteractionOption.Duplicate);
          setCurrentZap(zap);
          setCurrentModal(ZapModal.Duplicate);
        },
        disabled: !canDuplicate,
      },
      {
        icon: "navTemplate",
        label: t("create-template"),
        href: getCreateTemplateFromZapUrl(zapId, templateMode),
        onClick: () => {
          emit(ZapKebabInteractionOption.CreateTemplate);
        },
        disabled: !canCreateTemplate,
      },
      canMoveZapToFolder
        ? {
            icon: "actionMoveToFolder",
            label: tAction("move-to-folder"),
            onClick: () => {
              emit(ZapKebabInteractionOption.MoveToFolder);
              setCurrentZap(zap);
              setCurrentModal(ZapModal.Move);
            },
          }
        : undefined,
      {
        icon: "personSettings",
        label: tAction("change-owner"),
        onClick: () => {
          emit(ZapKebabInteractionOption.ChangeOwner);
          setCurrentZap(zap);
          setCurrentModal(ZapModal.ChangeOwner);
        },
        disabled: !canChangeOwner,
      },
      showDeleteAction || onCopyToAnotherAccount
        ? { divider: true }
        : undefined,
      onCopyToAnotherAccount
        ? {
            icon: "arrowExit",
            label: tAction("copy-to-another-account"),
            onClick: () => {
              onCopyToAnotherAccount(zap as HydratedZapAsset);
            },
          }
        : undefined,
      onCopyToAnotherAccount && showDeleteAction
        ? { divider: true }
        : undefined,
      showDeleteAction
        ? {
            icon: "actionTrash",
            label: t("delete-label", {
              isTrashed: String(!!isZapPurgeEligible),
            }),
            onClick: () => {
              emit(ZapKebabInteractionOption.Delete);
              setCurrentZap(zap);
              setCurrentModal(ZapModal.Delete);
            },
            disabled: !isZapDeleteEnabled,
            hasWarning: true,
          }
        : undefined,
    ].filter(Boolean) as KebabMenuItem[];
  }, [
    canChangeOwner,
    canCreateTemplate,
    canDuplicate,
    canMoveZapToFolder,
    canRename,
    canViewZapHistory,
    emit,
    isLastRunEnabled,
    isZapDeleteEnabled,
    isZapPurgeEligible,
    lastRun,
    onCopyToAnotherAccount,
    setCurrentModal,
    setCurrentZap,
    showDeleteAction,
    t,
    tAction,
    templateMode,
    zap,
    zapId,
  ]);

  return {
    menuItems,
    canEnableDisable,
    triggerPermissionsCheck: enableAdvancedPermissions,
  };
}
